import React, { useCallback, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { AS_THEME } from "./config/constants";
import { ClavaRootContext } from "./config/contexts";
import "./css/style.css";
import { ClavaRootContextType } from "./config/types";
import Main from "./views/navigation/Main";
import { store } from "./store";
import Loading from "./views/components/Loading";

import { fb, initFb } from "./config/firebase";

const rootClasses = document.body.className;

const App: React.FC = () => {
  const storedTheme = window.localStorage.getItem(AS_THEME);
  const storedThemeValid = storedTheme === "dark" || storedTheme === "light";
  const systemPrefersDark =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const initialTheme: "dark" | "light" = storedThemeValid
    ? (storedTheme as "dark" | "light")
    : systemPrefersDark
    ? "dark"
    : "light";

  const [theme, setTheme] = useState<"dark" | "light">(initialTheme);
  const [hasUserPreference, setHasUserPreference] =
    useState<boolean>(storedThemeValid);

  const setThemeCont = useCallback((newTheme: "dark" | "light") => {
    window.localStorage.setItem(AS_THEME, newTheme);
    document.body.className = `${rootClasses} ${newTheme}`;
    setTheme(newTheme);
    setHasUserPreference(true);
  }, []);

  useEffect(() => {
    document.body.className = `${rootClasses} ${initialTheme}`;
  }, [initialTheme]);

  useEffect(() => {
    const mql = window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
    let cleanup: (() => void) | undefined;
    if (!hasUserPreference && mql) {
      const handler = (e: MediaQueryListEvent) => {
        const nextTheme: "dark" | "light" = e.matches ? "dark" : "light";
        document.body.className = `${rootClasses} ${nextTheme}`;
        setTheme(nextTheme);
      };
      try {
        mql.addEventListener("change", handler);
        cleanup = () => mql.removeEventListener("change", handler);
      } catch (_) {
        // Safari <14
        // @ts-ignore deprecated
        mql.addListener(handler);
        // @ts-ignore deprecated
        cleanup = () => mql.removeListener(handler);
      }
    }
    return cleanup;
  }, [hasUserPreference]);
  const [rootContext, setRootContext] = useState<ClavaRootContextType>({
    theme,
    fbToken: "",
    initialized: true,
    setTheme: setThemeCont,
  });
    useEffect(() => {
        initFb().then(() => {
            const fbToken = fb().getFirebaseToken();
            setRootContext({
                theme,
                fbToken,
                initialized: true,
                setTheme: setThemeCont,
            });
        });
    }, [theme, setThemeCont]);
  if (rootContext.initialized) {
    return (
      <ClavaRootContext.Provider value={rootContext}>
        <Provider store={store}>
          <BrowserRouter>
            <Main />
          </BrowserRouter>
        </Provider>
      </ClavaRootContext.Provider>
    );
  }
  return <Loading />;
};

export default App;
